/**
 * 海螺音乐生成节点 - 接收两个文本输入生成音乐（Minimax）
 * 
 * 功能说明：
 * 1. 接收音乐描述文本节点（prompt）
 * 2. 接收歌词文本节点（lyrics）
 * 3. 配置音频参数（采样率、比特率、格式）
 * 4. 调用 Minimax 音乐生成 API
 * 5. 创建音频任务节点展示结果
 * 
 * 连接说明：
 * - 输入1：文本输入节点（音乐描述）- 必需
 * - 输入2：文本输入节点（歌词）- 必需
 * - 输出：音频任务节点
 */
import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { useReactFlow, useStore, Handle, Position } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import voiceService from '../../../api/VoiceService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import { TaskPricePreview } from './TaskPricePreview';
import { useTaskQuote } from './useTaskQuote';
import './HailuoMusicNode.css';

export const HailuoMusicNode = memo(({ id, data }) => {
  const { updateNodeData, getNode } = useReactFlow();
  const { addNodesWithEdges, duplicateNode, createNodeAndConnect } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const canvasNodes = useStore((state) => state.nodes);
  const { readOnly } = useProject();
  
  // 基础配置
  const [model] = useState('music-2.0'); // 固定模型
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  
  // 高级设置
  const [showAdvanced, setShowAdvanced] = useState(data.showAdvanced || false);
  const [sampleRate, setSampleRate] = useState(data.sampleRate || 44100);
  const [bitrate, setBitrate] = useState(data.bitrate || 256000);
  const [format, setFormat] = useState(data.format || 'mp3');

  // 同步节点数据
  useEffect(() => {
    updateNodeData(id, {
      showAdvanced,
      sampleRate,
      bitrate,
      format,
    });
  }, [id, showAdvanced, sampleRate, bitrate, format, updateNodeData]);


  const connectedPrompt = useMemo(() => {
    const edge = edges.find(item => item.target === id && item.targetHandle === 'prompt');
    return canvasNodes.find(node => node.id === edge?.source)?.data?.prompt || null;
  }, [canvasNodes, edges, id]);

  const connectedLyrics = useMemo(() => {
    const edge = edges.find(item => item.target === id && item.targetHandle === 'lyrics');
    return canvasNodes.find(node => node.id === edge?.source)?.data?.prompt || null;
  }, [canvasNodes, edges, id]);

  const inputParams = useMemo(() => ({
    model,
    prompt: (connectedPrompt || '').trim(),
    lyrics: (connectedLyrics || '').trim(),
    audio_setting: {
      sample_rate: sampleRate,
      bitrate,
      format,
    },
    output_format: 'url',
  }), [bitrate, connectedLyrics, connectedPrompt, format, model, sampleRate]);

  const quoteEnabled = Boolean(
    connectedPrompt?.trim().length >= 10
    && connectedPrompt.trim().length <= 2000
    && connectedLyrics?.trim().length >= 10
    && connectedLyrics.trim().length <= 3000
  ) && !readOnly;
  const taskQuote = useTaskQuote({
    enabled: quoteEnabled,
    apiVersion: 'v2',
    appName: 'minimax/music-gen',
    taskType: 'minimax_api',
    params: inputParams,
  });

  // 验证连接
  const isValidConnection = useCallback((connection) => {
    console.log('HailuoMusicNode - 验证连接:', {
      targetHandle: connection.targetHandle,
      source: connection.source,
      target: connection.target
    });
    
    if (connection.targetHandle === 'prompt' || connection.targetHandle === 'lyrics') {
      const sourceNode = getNode(connection.source);
      console.log('HailuoMusicNode - 源节点类型:', sourceNode?.type);
      const isValid = sourceNode?.type === 'promptInput';
      console.log('HailuoMusicNode - 连接是否有效:', isValid);
      return isValid;
    }
    return true;
  }, [getNode]);

  // 处理 Handle 标签点击 - 自动创建节点并连接
  const handleHandleLabelClick = useCallback((e, handleId, handleType) => {
    e.stopPropagation();  // 阻止事件冒泡
    
    // 只读模式下禁用
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    
    // 调用统一的创建方法（会自动从配置读取限制）
    createNodeAndConnect(id, 'hailuoMusic', handleId, handleType);
  }, [id, readOnly, createNodeAndConnect]);

  // 生成音乐
  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);
      
      // 1. 获取音乐描述
      const musicPrompt = connectedPrompt;
      if (!musicPrompt || musicPrompt.trim().length < 10) {
        throw new Error('请连接音乐描述文本节点，且内容至少需要10个字符');
      }
      
      if (musicPrompt.trim().length > 2000) {
        throw new Error('音乐描述最多2000个字符');
      }
      
      // 2. 获取歌词
      const lyrics = connectedLyrics;
      if (!lyrics || lyrics.trim().length < 10) {
        throw new Error('请连接歌词文本节点，且内容至少需要10个字符');
      }
      
      if (lyrics.trim().length > 3000) {
        throw new Error('歌词最多3000个字符');
      }
      
      // 3. 获取用户 token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        throw new Error('请先登录');
      }
      
      console.log('音乐生成参数:', inputParams);

      const confirmedQuote = await taskQuote.quoteNow();
      // 5. 创建任务
      const taskData = await voiceService.createTask('minimax/music-gen', inputParams);
      
      if (taskData && taskData.task_id) {
        toast.success('音乐生成任务已创建！');
        
        // 6. 创建任务节点（使用 audioTask 类型，因为音乐也是音频）
        const currentNode = getNode(id);
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'audioTask',
          position: {
            x: currentNode.position.x + 450,
            y: currentNode.position.y,
          },
          data: {
            taskId: taskData.task_id,
            taskType: 'music-generation',
            status: taskData.status || 'pending',
            inputParams: inputParams,
            chargedPoints: Number(taskData.money ?? confirmedQuote.points),
          },
        };
        
        // 7. 创建连接边
        const newEdge = {
          id: nanoid(),
          source: id,
          target: taskNodeId,
          type: 'default',
        };
        
        // 8. 添加节点和边
        await addNodesWithEdges([taskNode], [newEdge]);
        
        setIsGenerating(false);
      } else {
        throw new Error('创建任务失败');
      }
      
    } catch (err) {
      console.error('音乐生成失败:', err);
      const errorMessage = err?.message || '生成失败，请重试';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsGenerating(false);
    }
  };

  // 复制节点
  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateNode(id);
  };

  return (
    <div className="HailuoMusicNode-container">
      {/* 复制按钮 */}
      <button 
        className="HailuoMusicNode-duplicate-button"
        onClick={handleDuplicate}
        title="复制节点"
      >
        <i className="bi bi-copy"></i>
      </button>

      {/* 音乐描述输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="prompt"
        className="HailuoMusicNode-handle"
        style={{ top: '35%' }}
        isValidConnection={isValidConnection}
      >
        <div 
          className="HailuoMusicNode-handle-label HailuoMusicNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'prompt', 'text')}
          title="点击自动创建文本输入节点"
        >
          音乐描述
        </div>
      </Handle>

      {/* 歌词输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="lyrics"
        className="HailuoMusicNode-handle"
        style={{ top: '65%' }}
        isValidConnection={isValidConnection}
      >
        <div 
          className="HailuoMusicNode-handle-label HailuoMusicNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'lyrics', 'text')}
          title="点击自动创建文本输入节点"
        >
          歌词
        </div>
      </Handle>

      {/* 输出 Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="HailuoMusicNode-handle"
      />

      <div className="HailuoMusicNode-title">音乐生成（海螺）</div>

      <div className="HailuoMusicNode-wrapper">
        {/* 音乐描述输入状态 */}
        <div className="HailuoMusicNode-field">
          <div className="HailuoMusicNode-label-with-hint">
            <label className="HailuoMusicNode-label">音乐描述</label>
            <span className="HailuoMusicNode-label-hint">上方接口 · 10-2000字符</span>
          </div>
          {connectedPrompt ? (
            <div className="HailuoMusicNode-connected-text">
              {connectedPrompt.substring(0, 50)}
              {connectedPrompt.length > 50 ? '...' : ''}
            </div>
          ) : (
            <div className="HailuoMusicNode-placeholder">
              📝 未连接文本输入节点
            </div>
          )}
        </div>

        {/* 歌词输入状态 */}
        <div className="HailuoMusicNode-field">
          <div className="HailuoMusicNode-label-with-hint">
            <label className="HailuoMusicNode-label">歌词</label>
            <span className="HailuoMusicNode-label-hint">下方接口 · 10-3000字符</span>
          </div>
          {connectedLyrics ? (
            <div className="HailuoMusicNode-connected-text">
              {connectedLyrics.substring(0, 50)}
              {connectedLyrics.length > 50 ? '...' : ''}
            </div>
          ) : (
            <div className="HailuoMusicNode-placeholder">
              🎤 未连接文本输入节点
            </div>
          )}
        </div>

        {/* 模型显示 */}
        <div className="HailuoMusicNode-field">
          <label className="HailuoMusicNode-label">模型</label>
          <div className="HailuoMusicNode-model-display">
            {model}
          </div>
        </div>

        {/* 高级设置开关 */}
        <div className="HailuoMusicNode-advanced-toggle">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            disabled={readOnly}
            className="HailuoMusicNode-toggle-button"
          >
            {showAdvanced ? '▼' : '▶'} 高级设置
          </button>
        </div>

        {/* 高级设置面板 */}
        {showAdvanced && (
          <div className="HailuoMusicNode-advanced">
            {/* 采样率 */}
            <div className="HailuoMusicNode-field">
              <label className="HailuoMusicNode-label">采样率</label>
              <select
                value={sampleRate}
                onChange={(e) => setSampleRate(parseInt(e.target.value))}
                disabled={isGenerating || readOnly}
                className="HailuoMusicNode-select"
              >
                <option value="16000">16000 Hz</option>
                <option value="24000">24000 Hz</option>
                <option value="32000">32000 Hz</option>
                <option value="44100">44100 Hz（推荐）</option>
              </select>
            </div>

            {/* 比特率 */}
            <div className="HailuoMusicNode-field">
              <label className="HailuoMusicNode-label">比特率</label>
              <select
                value={bitrate}
                onChange={(e) => setBitrate(parseInt(e.target.value))}
                disabled={isGenerating || readOnly}
                className="HailuoMusicNode-select"
              >
                <option value="32000">32 kbps</option>
                <option value="64000">64 kbps</option>
                <option value="128000">128 kbps</option>
                <option value="256000">256 kbps（推荐）</option>
              </select>
            </div>

            {/* 格式 */}
            <div className="HailuoMusicNode-field">
              <label className="HailuoMusicNode-label">格式</label>
              <select
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                disabled={isGenerating || readOnly}
                className="HailuoMusicNode-select"
              >
                <option value="mp3">MP3</option>
                <option value="wav">WAV</option>
                <option value="pcm">PCM</option>
              </select>
            </div>
          </div>
        )}

        {/* 连接说明 */}
        <div className="HailuoMusicNode-connection-guide">
          <div className="HailuoMusicNode-guide-title">💡 连接说明</div>
          <div className="HailuoMusicNode-guide-item">
            <span className="HailuoMusicNode-guide-icon">📝</span>
            <span className="HailuoMusicNode-guide-text">上方接口：音乐描述 (10-2000字符)</span>
          </div>
          <div className="HailuoMusicNode-guide-item">
            <span className="HailuoMusicNode-guide-icon">🎤</span>
            <span className="HailuoMusicNode-guide-text">下方接口：歌词 (10-3000字符)</span>
          </div>
        </div>

        <TaskPricePreview quote={taskQuote} />

        {/* 生成按钮 */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating || readOnly || (quoteEnabled && taskQuote.status !== 'success')}
          className={`HailuoMusicNode-generate ${
            isGenerating || readOnly || (quoteEnabled && taskQuote.status !== 'success')
              ? 'HailuoMusicNode-generate-disabled'
              : ''
          }`}
        >
          {isGenerating ? '创建中...' : '创建任务'}
        </button>

        {/* 错误提示 */}
        {error && (
          <div className="HailuoMusicNode-error">
            ❌ {error}
          </div>
        )}
      </div>
    </div>
  );
});

HailuoMusicNode.displayName = 'HailuoMusicNode';
